import { useMemo } from 'react';
import {
  AssignmentCategory,
  StudentAssignmentItem,
} from '../../../constants/types/student/homework/type';
import TextWinds from '../../common/TextWinds';
import StudentAssignmentCard from './StudentAssignmentCard';
import StudentRecitationAssignmentCard from './StudentRecitationAssignmentCard';

interface StudentAssignmentListPanelProps {
  selectedCategory: AssignmentCategory;
  assignmentList: StudentAssignmentItem[];
  fetchAssignmentInfo: () => Promise<void>;
}

/**
 * 개편 LMS (학생) - 클래스 상세 페이지 > 콘텐츠 판넬 > 우측 [할 일 목록]
 */
const StudentAssignmentListPanel = ({
  selectedCategory,
  assignmentList,
  fetchAssignmentInfo,
}: StudentAssignmentListPanelProps) => {
  const filteredAssignmentList = useMemo(() => {
    switch (selectedCategory) {
      case 'JEBSON':
        return assignmentList.filter((assignment) =>
          ['EC', 'EF', 'ESB'].includes(assignment.type),
        );

      case 'RECITATION':
        return assignmentList.filter((assignment) =>
          ['JEBS_AI_RECITATION'].includes(assignment.type),
        );

      default:
        return assignmentList;
    }
  }, [selectedCategory, assignmentList]);

  const urgentAssignmentList = filteredAssignmentList.filter(
    (assignment) => assignment.isUrgent,
  );

  const normalAssignmentList = filteredAssignmentList.filter(
    (assignment) => !assignment.isUrgent,
  );

  const hasAssignment =
    urgentAssignmentList.length > 0 || normalAssignmentList.length > 0;

  if (!hasAssignment) {
    return (
      <div className="flex h-[280px] items-center justify-center rounded-xl border border-dashed border-gray4 bg-gray0 px-4 text-center">
        <TextWinds type="content_body2" color="gray6">
          등록된 과제가 없습니다.
        </TextWinds>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {urgentAssignmentList.length > 0 && (
        <section>
          <div className="mb-3 flex items-center gap-2">
            <TextWinds type="content_body2" color="gray8" className="font-bold">
              마감 임박
            </TextWinds>

            <span className="rounded-full bg-red-50 px-2 py-1 text-xs font-bold text-red-500">
              서둘러요!
            </span>
          </div>

          <div className="flex flex-col gap-3">
            {urgentAssignmentList.map((assignment) => {
              return selectedCategory !== 'RECITATION' ? (
                <StudentAssignmentCard
                  key={assignment.todoId}
                  assignment={assignment}
                  variant={'urgent'}
                  fetchAssignmentInfo={fetchAssignmentInfo}
                />
              ) : (
                <StudentRecitationAssignmentCard
                  key={assignment.todoId}
                  assignment={assignment}
                  variant={'urgent'}
                />
              );
            })}
          </div>
        </section>
      )}

      {normalAssignmentList.length > 0 && (
        <section>
          <div className="mb-3 flex items-center gap-2">
            <TextWinds type="content_body2" color="gray8" className="font-bold">
              진행 중
            </TextWinds>
          </div>

          <div className="flex flex-col gap-3">
            {normalAssignmentList.map((assignment) => {
              return selectedCategory !== 'RECITATION' ? (
                <StudentAssignmentCard
                  key={assignment.todoId}
                  assignment={assignment}
                  variant={'normal'}
                  fetchAssignmentInfo={fetchAssignmentInfo}
                />
              ) : (
                <StudentRecitationAssignmentCard
                  key={assignment.todoId}
                  assignment={assignment}
                  variant={'normal'}
                />
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default StudentAssignmentListPanel;
